// src/pages/Admin/AdminAllergensPage.jsx
import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import api from "../../services/api";

const AdminAllergensPage = () => {
  const [allergens, setAllergens] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    icon: "",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const fetchAllergens = async () => {
    const res = await api.get("/admin/allergens");
    setAllergens(res.data);
  };

  useEffect(() => {
    fetchAllergens();
  }, []);

  const handleSubmit = async () => {
    try {
      if (editing) {
        await api.put(`/admin/allergens/${editing._id}`, formData);
        setSnackbar({
          open: true,
          message: "Allergen updated",
          severity: "success",
        });
      } else {
        await api.post("/admin/allergens", formData);
        setSnackbar({
          open: true,
          message: "Allergen created",
          severity: "success",
        });
      }
      setOpen(false);
      setEditing(null);
      setFormData({ name: "", description: "", icon: "" });
      fetchAllergens();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: "error" });
    }
  };

  const handleDelete = async (id) => {
    await api.delete(`/admin/allergens/${id}`);
    fetchAllergens();
    setSnackbar({ open: true, message: "Allergen deleted", severity: "info" });
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Manage Allergens
      </Typography>
      <Button variant="contained" color="primary" onClick={() => setOpen(true)}>
        Add Allergen
      </Button>

      <TableContainer component={Paper} sx={{ mt: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Icon</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {allergens.map((allergen) => (
              <TableRow key={allergen._id}>
                <TableCell>{allergen.name}</TableCell>
                <TableCell>{allergen.description}</TableCell>
                <TableCell>{allergen.icon}</TableCell>
                <TableCell align="right">
                  <IconButton
                    onClick={() => {
                      setEditing(allergen);
                      setFormData(allergen);
                      setOpen(true);
                    }}>
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(allergen._id)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>{editing ? "Edit Allergen" : "Add Allergen"}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            sx={{ my: 1 }}
          />
          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            sx={{ my: 1 }}
          />
          <TextField
            fullWidth
            label="Icon (optional emoji or icon name)"
            value={formData.icon}
            onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
            sx={{ my: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {editing ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminAllergensPage;
